import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { useWorldStore } from '../state/worldStore';
import { useCityStore } from '../city/cityStore';
import { stateAt } from '../city/cityMotion';
import { serverNow, cityEngage, citySocketId, busyLine } from '../services/worldSocket';
import { Performer } from './VenueStage';
import WorldLabel from './WorldLabel';
import { labelStyle } from './Npc';
import { bodyScene } from './SceneChat';

// RoomPerson — an artist IN a room you can walk up to (2026-11-01): a guest
// standing about (RoomGuests) or the owner in at their own room (RoomHost).
//
// Khari: "I am unable to speak with artists while they are in the blackroom.
// there is one in here now. There is no status showing for her, so I don't know
// if she is busy, or broken." Both were true of the room and neither of the
// street: the room drew a bare name, and a tap the city refused did nothing at
// all. So this says what the street says (CityResident) — "💬 Kelsey — tap to
// talk", "· in conversation", "· talking with someone" — and a refused tap says
// why (busyLine) for a few seconds, instead of nothing.
//
// The body is VenueStage's Performer at `mark` (room space, × `k` like the booth).
const NOTE_MS = 3200;

export default function RoomPerson({ avatarId, name, mark, k = 1, light }) {
  const openChat = useWorldStore((s) => s.openChat);
  const npcList = useWorldStore((s) => s.npcList);
  const inChat = useWorldStore((s) => String(s.chatAvatar?.avatarId ?? '') === String(avatarId));
  const goal = useRef(null);   // they stay on their mark (and turn to you while you talk)
  const bodyRef = useRef(null);
  const camera = useThree((s) => s.camera);
  const [status, setStatus] = useState('free');   // 'free' | 'mine' | 'held' | 'talking'
  const [note, setNote] = useState(null);
  const first = String(name || '').trim().split(/\s+/)[0] || 'They';
  const height = useMemo(() => (npcList || []).find((n) => Number(n?.avatarId) === Number(avatarId) && n.rigged && !n.test)?.height || 0.75, [npcList, avatarId]);

  // From the shared city state, once a second: talking, held by someone, or free.
  useEffect(() => {
    const check = () => {
      const cs = useCityStore.getState();
      const t = serverNow();
      const r = cs.byId?.[avatarId];
      const st = r ? stateAt(r, t, cs.convs) : null;
      const h = cs.holds?.[avatarId];
      const held = !!(h && t >= h.since && t < h.until);
      const next = st?.convId ? 'talking' : held ? (h.by && h.by === citySocketId() ? 'mine' : 'held') : 'free';
      setStatus((s) => (s === next ? s : next));
    };
    check();
    const tm = setInterval(check, 1000);
    return () => clearInterval(tm);
  }, [avatarId]);
  useEffect(() => {
    if (!note) return undefined;
    const tm = setTimeout(() => setNote(null), NOTE_MS);
    return () => clearTimeout(tm);
  }, [note]);

  // A tap: they stop for you and the chat opens; a no from the city says why.
  const onTap = useCallback(async () => {
    if (status === 'talking') { setNote(busyLine(first, 'talking')); return; }
    const r = await cityEngage(avatarId);
    if (!r?.ok) { setNote(busyLine(first, r?.reason, r?.activity)); return; }
    // AS A SCENE (components/SceneChat): they turn to you where they stand, and
    // the camera comes in from your side of them — open floor, wherever their
    // mark was facing.
    const grp = bodyRef.current;
    if (grp?.parent) {
      const me = grp.parent.worldToLocal(camera.getWorldPosition(new THREE.Vector3()));
      goal.current = { x: grp.position.x, y: grp.position.y, z: grp.position.z, face: { x: me.x, z: me.z } };
    }
    openChat({ avatarId, name, city: { mustGoAt: r.mustGoAt || null, nextActivity: r.nextActivity || null }, scene: bodyScene(grp, { avatarId, height }) });
  }, [status, first, avatarId, name, openChat, camera, height]);
  // The chat closed: back to the way their mark faces.
  useEffect(() => { if (!inChat) goal.current = null; }, [inChat]);

  const text = note
    || (status === 'free' || status === 'mine' ? `💬 ${first} — tap to talk`
      : status === 'talking' ? `${first} · in conversation`
        : `${first} · talking with someone`);
  const at = [mark.pos[0] * k, (mark.pos[1] || 0) * k, mark.pos[2] * k];

  return (
    <group>
      <Performer a={avatarId} name={name} mark={mark} k={k} light={light} talking={false} goal={goal} onTap={onTap} groupRef={bodyRef} />
      {!inChat && (
        <WorldLabel position={[at[0], at[1] + height + 0.32, at[2]]} center occlude={false} style={{ pointerEvents: 'none' }}>
          <div style={{ ...labelStyle(status === 'free' && !note), font: '600 11.5px system-ui, sans-serif', padding: '3px 9px', transform: 'none', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</div>
        </WorldLabel>
      )}
    </group>
  );
}
